import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { ScrollView, VStack } from "native-base";
import { currentPregnancyWeek, differenceInWeeks, invariant, } from "@madmedical/utils";
import { useTranslation } from "@madmedical/i18n";
import Breadcrumb from "../../molecules/Breadcrumb";
import Headline from "../../molecules/Headline";
import PregnancyPageTabs from "../../organisms/Pregnancy/PregnancyPageTabs";
import PregnancyGraphWeightMeasure from "../../molecules/Pregnancy/PregnancyGraphWeightMeasure";
import PregnancyCircumferenceMeasure from "../../molecules/Pregnancy/PregnancyCircumferenceMeasure";
import { usePregnancy } from "../../providers/pregnancy";
const PregnancyMeasurementPageContent = ({ onMeasurePress, isDoctor = false, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { weightData, bmiData, circumferenceData, patient, pregnancyData, firstMeasuredBmi, firstMeasuredWeight, } = usePregnancy();
    const { t } = useTranslation();
    const pregnancyStart = (_a = pregnancyData === null || pregnancyData === void 0 ? void 0 : pregnancyData.startedAt) !== null && _a !== void 0 ? _a : new Date().toISOString();
    const currentWeek = currentPregnancyWeek(new Date(pregnancyStart), new Date());
    const weightItems = weightData === null || weightData === void 0 ? void 0 : weightData.items;
    const calculateWeight = weightItems === null || weightItems === void 0 ? void 0 : weightItems.map((item) => {
        const { metric, measuredAt } = item;
        const pregnancyWeek = differenceInWeeks(new Date(pregnancyStart), new Date(measuredAt));
        return {
            valueWeight: Math.round(metric.kg),
            measuredAt,
            pregnancyWeek,
        };
    });
    const bmiItems = bmiData === null || bmiData === void 0 ? void 0 : bmiData.items;
    const calculateBmi = bmiItems === null || bmiItems === void 0 ? void 0 : bmiItems.map((item) => {
        const { metric, measuredAt } = item;
        const pregnancyWeek = differenceInWeeks(new Date(pregnancyStart), new Date(measuredAt));
        return {
            valueBmi: Math.round(metric.bmi),
            measuredAt,
            pregnancyWeek,
        };
    });
    const weightPointData = calculateWeight.map((a) => (Object.assign(Object.assign({}, a), calculateBmi.find((b) => b.pregnancyWeek === a.pregnancyWeek))));
    const circumferenceItems = circumferenceData === null || circumferenceData === void 0 ? void 0 : circumferenceData.items;
    const circumferencePointData = circumferenceItems === null || circumferenceItems === void 0 ? void 0 : circumferenceItems.map((item) => {
        const { metric, measuredAt } = item;
        const pregnancyWeek = differenceInWeeks(new Date(pregnancyStart), new Date(measuredAt));
        return {
            valueCircumference: Math.round(metric.circumference),
            measuredAt,
            pregnancyWeek,
        };
    });
    const weightStats = weightData === null || weightData === void 0 ? void 0 : weightData.stats;
    const bmiStats = bmiData === null || bmiData === void 0 ? void 0 : bmiData.stats;
    const circumferenceStats = circumferenceData === null || circumferenceData === void 0 ? void 0 : circumferenceData.stats;
    const roundMin = useCallback((number) => Math.floor(number), []);
    const roundMax = useCallback((number) => Math.ceil((number + 2) / 10) * 10, []);
    const weightMin = useMemo(() => {
        var _a, _b;
        return firstMeasuredWeight && firstMeasuredWeight < ((_a = weightStats === null || weightStats === void 0 ? void 0 : weightStats.min) === null || _a === void 0 ? void 0 : _a.kg)
            ? firstMeasuredWeight
            : (_b = weightStats === null || weightStats === void 0 ? void 0 : weightStats.min) === null || _b === void 0 ? void 0 : _b.kg;
    }, [(_b = weightStats === null || weightStats === void 0 ? void 0 : weightStats.min) === null || _b === void 0 ? void 0 : _b.kg, firstMeasuredWeight]);
    const bmiMin = useMemo(() => {
        var _a, _b;
        return firstMeasuredBmi && firstMeasuredBmi < ((_a = bmiStats === null || bmiStats === void 0 ? void 0 : bmiStats.min) === null || _a === void 0 ? void 0 : _a.bmi)
            ? firstMeasuredBmi
            : (_b = bmiStats === null || bmiStats === void 0 ? void 0 : bmiStats.min) === null || _b === void 0 ? void 0 : _b.bmi;
    }, [(_c = bmiStats === null || bmiStats === void 0 ? void 0 : bmiStats.min) === null || _c === void 0 ? void 0 : _c.bmi, firstMeasuredBmi]);
    const weightGraphRanges = {
        weight: {
            min: roundMin(weightMin),
            max: (weightStats === null || weightStats === void 0 ? void 0 : weightStats.max) ? roundMax((_d = weightStats === null || weightStats === void 0 ? void 0 : weightStats.max) === null || _d === void 0 ? void 0 : _d.kg) : 10,
        },
        bmi: {
            min: roundMin(bmiMin),
            max: (bmiStats === null || bmiStats === void 0 ? void 0 : bmiStats.max) ? roundMax((_e = bmiStats === null || bmiStats === void 0 ? void 0 : bmiStats.max) === null || _e === void 0 ? void 0 : _e.bmi) : 10,
        },
        percentile: {
            min: roundMin(weightMin),
            max: weightMin + 16,
        },
    };
    const circumferenceGraphRanges = {
        circumference: {
            min: (circumferenceStats === null || circumferenceStats === void 0 ? void 0 : circumferenceStats.min)
                ? roundMin((_f = circumferenceStats === null || circumferenceStats === void 0 ? void 0 : circumferenceStats.min) === null || _f === void 0 ? void 0 : _f.circumference)
                : 0,
            max: (circumferenceStats === null || circumferenceStats === void 0 ? void 0 : circumferenceStats.max)
                ? roundMax((_g = circumferenceStats === null || circumferenceStats === void 0 ? void 0 : circumferenceStats.max) === null || _g === void 0 ? void 0 : _g.circumference)
                : 10,
        },
    };
    /* ranges for weight gain
    const firstTrimesterGain = [0.5, 2];
    const normalGain = [0.35, 0.5];

    const gainRanges = {
        underweight: [0.44, 0.58],
        normal: [0.35, 0.5],
        overweight: [0.23, 0.33],
        obese: [0.17, 0.27],
    };
    */
    const normalWeightGain = [
        {
            pregnancyWeek: 0,
            valuePercentileMin: weightMin,
            valuePercentileMax: weightMin,
        },
        {
            pregnancyWeek: 1,
            valuePercentileMin: weightMin,
            valuePercentileMax: weightMin,
        },
        {
            pregnancyWeek: 13,
            valuePercentileMin: weightMin + 0.9,
            valuePercentileMax: weightMin + 2.5,
        },
        {
            pregnancyWeek: 40,
            valuePercentileMin: weightMin + 11.3,
            valuePercentileMax: weightMin + 15.9,
        },
    ];
    invariant(weightPointData);
    invariant(circumferencePointData);
    return (_jsxs(ScrollView, { children: [isDoctor ? (_jsx(Headline, { minHeight: 12, borderBottomWidth: 1, borderBottomColor: "gray.100", borderBottomStyle: "solid", leftContent: _jsxs(Breadcrumb, { children: [_jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Link, Object.assign({ route: "dashboard", params: {} }, { children: t("health") })) }), patient && (_jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Link, Object.assign({ route: "patient", params: { patientId: patient.userId } }, { children: patient.fullName })) })), _jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Text, { children: t("pregnancy:pregnancy2") }) })] }) })) : (_jsx(Headline, { minHeight: 12, borderBottomWidth: 1, borderBottomColor: "gray.100", borderBottomStyle: "solid", leftContent: _jsxs(Breadcrumb, { children: [_jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Link, Object.assign({ route: "dashboard", params: {} }, { children: t("health") })) }), _jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Text, { children: t("pregnancy:pregnancy2") }) })] }) })), _jsxs(VStack, { children: [_jsx(PregnancyPageTabs, { isDoctor: isDoctor }), _jsx(PregnancyGraphWeightMeasure, { onMeasurePress: onMeasurePress, weightPointData: weightPointData, ranges: weightGraphRanges, isDoctor: isDoctor, currentWeek: currentWeek, percentile: normalWeightGain }), _jsx(PregnancyCircumferenceMeasure, { onMeasurePress: onMeasurePress, circumferencePointData: circumferencePointData, ranges: circumferenceGraphRanges, isDoctor: isDoctor, currentWeek: currentWeek })] })] }));
};
export default PregnancyMeasurementPageContent;
